/**
 * Trades Poster — Trade Categories
 * Document type: Source register
 * Status: READY — Trade Categories register specification
 *
 * Creates, displays, edits, saves and retains Trade Category and Subcategory
 * records. Public-facing uses the original Subcategory ID (no separate ID).
 * Existing names accept spelling corrections only. No Delete action.
 */

import { emitTradeCategoryHistory } from './changeHistoryEmitters';

export const SUPPORTED_ICON_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'];
export const SUPPORTED_ICON_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.svg'];

export const LIST_PREVIEW_COUNT = 2;
export const INITIAL_SUBCATEGORY_BLOCKS = 1;

export const TRADE_CATEGORY_ROUTE = '/admin/trade-categories';
export const DEFAULT_ACTOR = 'Platform Owner';

export const FORM_MODE = {
  ADD: 'add',
  EDIT: 'edit',
  /** @deprecated Prefer FORM_MODE.ADD */
  ADD_CATEGORY: 'add',
  /** @deprecated View removed from Trade Categories register */
  VIEW: 'view',
  /** @deprecated Separate modes removed; use Edit */
  ADD_SUBCATEGORY: 'add-subcategory',
  SELECT_PUBLIC_FACING: 'select-public-facing',
  CORRECT_CATEGORY_SPELLING: 'correct-category-spelling',
  CORRECT_SUBCATEGORY_SPELLING: 'correct-subcategory-spelling',
};

export const SAVE_MESSAGES = {
  created: 'Trade Category created successfully.',
  updated: 'Trade Category updated successfully.',
  /** @deprecated Prefer created */
  success: 'Trade Category created successfully.',
  failure: 'Trade Category could not be saved. Review the highlighted fields and try again.',
  concurrent: 'This record has changed. Review the latest record.',
};

export const CATEGORY_STATUS = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
};

export const EMPTY_TABLE_MESSAGE = 'No Trade Categories have been added.';

export const ID_PLACEHOLDER = 'Generated after Save';
export const AUDIT_PLACEHOLDER = 'Recorded after Save';
export const NOT_APPLICABLE = 'Not applicable';

export const SAMPLE_VALUES = {
  tradeCategoryName: 'Plumbing',
  tradeCategoryIcon: 'plumbing-icon.svg',
  subcategoryName: 'General Plumbing',
  publicFacingSubcategoryName: 'Gas Fitting',
  publicFacingIcon: 'gas-fitting-icon.svg',
  addSubcategoryName: 'Roof Plumbing',
  addSubcategoryPublicIcon: 'roof-plumbing-icon.svg',
};

export const FIELD_ERRORS = {
  categoryName: 'Enter a trade category name.',
  categoryIcon: 'Add a trade category icon.',
  atLeastOneSubcategory: 'Add at least one subcategory.',
  subcategoryName: 'Enter a subcategory name.',
  categoryNameExists: 'This trade category name already exists.',
  subcategoryNameExists: 'This subcategory name already exists.',
  subcategoryNameRepeated: 'This subcategory name already exists.',
  publicIcon: 'Add a public-facing icon.',
  concurrent: 'This record has changed. Review the latest record.',
  spellingOnly: 'Only a spelling correction is permitted. The record meaning cannot change.',
  unfinishedBlock: 'Complete or remove unfinished unsaved blocks before Save.',
};

/** @deprecated Kept for import compatibility. */
export const PERMANENT_NOTICE =
  'Saved records are retained. Existing names accept spelling corrections only.';

/** @deprecated Public-facing selections are reversible. */
export const PUBLIC_FACING_PERMANENT_NOTICE = '';

/** @deprecated Spelling corrections happen in Edit. */
export const SPELLING_CORRECTION_NOTICE =
  'Only the spelling will change. The permanent ID and record will remain unchanged.';

export const ACTIVITY_EVENTS = {
  CATEGORY_CREATED: 'Trade Category created',
  SUBCATEGORY_CREATED_WITH_CATEGORY: 'Subcategory created with a new Trade Category',
  SUBCATEGORY_ADDED: 'Subcategory added to an existing Trade Category',
  CATEGORY_UPDATED: 'Trade Category updated',
};

export const MEANING_MESSAGES = {
  tradeCategory:
    'This change alters the Trade Category meaning. Create a new Trade Category instead.',
  tradeSubcategory:
    'This change alters the represented service. Create a new Subcategory instead.',
  spellingOnly: FIELD_ERRORS.spellingOnly,
};

const ACTIVITY_LOG_KEY = 'tradesPoster.tradeCategoryActivityLog';

export const normName = (value) =>
  (value || '').trim().toLowerCase().replace(/\s+/g, ' ');

export const trimName = (value) => (value || '').trim().replace(/\s+/g, ' ');

export const formatTradeCategoryAuditDate = (date = new Date()) =>
  date.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

export const formatTradeCategoryListDate = (date = new Date()) => {
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return typeof date === 'string' && date ? date : '—';
  }
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = parsed.toLocaleString('en-AU', { month: 'short' });
  const year = parsed.getFullYear();
  return `${day} ${month} ${year}`;
};

function meaningCore(value) {
  return normName(value).replace(/[^a-z0-9]+/g, '');
}

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const rows = Array.from({ length: a.length + 1 }, (_, i) => i);
  for (let j = 1; j <= b.length; j += 1) {
    let prev = rows[0];
    rows[0] = j;
    for (let i = 1; i <= a.length; i += 1) {
      const temp = rows[i];
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      rows[i] = Math.min(rows[i] + 1, rows[i - 1] + 1, prev + cost);
      prev = temp;
    }
  }
  return rows[a.length];
}

/** True when a name correction looks like spelling/wording only, not a different service. */
export function retainsServiceMeaning(previousName, nextName) {
  const a = meaningCore(previousName);
  const b = meaningCore(nextName);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  const maxLen = Math.max(a.length, b.length);
  const allowed = Math.max(2, Math.floor(maxLen * 0.25));
  return levenshtein(a, b) <= allowed;
}

export function publicNameMatchesSource(publicName, sourceName) {
  return normName(publicName) === normName(sourceName);
}

export function isSavedTradeCategory(category) {
  return Boolean(category?.id);
}

export function isAppliedTradeCategory(category) {
  return isSavedTradeCategory(category);
}

export function getAppliedTradeCategories(tradeCategories) {
  return (tradeCategories || []).filter(isSavedTradeCategory);
}

export function nextTradeCategoryId(existing = []) {
  const nums = existing
    .map((c) => {
      const match = String(c.id || '').match(/^TC-(\d+)$/i);
      return match ? Number(match[1]) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `TC-${String(next).padStart(4, '0')}`;
}

export function nextTradeSubcategoryId(existingIds = []) {
  const nums = existingIds
    .map((id) => {
      const match = String(id || '').match(/^SC-(\d+)$/i);
      return match ? Number(match[1]) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `SC-${String(next).padStart(4, '0')}`;
}

/** @deprecated No Public-Facing IDs. Kept for import compatibility. */
export function nextPublicCategoryId(existingIds = []) {
  return nextTradeSubcategoryId(existingIds);
}

/** @deprecated Kept for import compatibility. */
export function nextPublicSubcategoryId(existingIds = []) {
  return nextTradeSubcategoryId(existingIds);
}

export function emptySubcategoryBlock(overrides = {}) {
  return {
    localKey: overrides.localKey || `sub-local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: '',
    name: '',
    displayAsPublicFacing: false,
    publicFacingName: '',
    publicFacingIconUrl: '',
    publicFacingIconFileName: '',
    createdOn: '',
    createdBy: '',
    lastUpdatedOn: '',
    lastUpdatedBy: '',
    ...overrides,
  };
}

export function createInitialSubcategoryBlocks(count = INITIAL_SUBCATEGORY_BLOCKS) {
  return Array.from({ length: Math.max(1, count) }, () => emptySubcategoryBlock());
}

function cloneSub(s = {}) {
  return {
    localKey: s.localKey || '',
    id: s.id || '',
    name: s.name || '',
    displayAsPublicFacing: Boolean(s.displayAsPublicFacing),
    publicFacingName: s.publicFacingName || '',
    publicFacingIconUrl: s.publicFacingIconUrl || '',
    publicFacingIconFileName: s.publicFacingIconFileName || '',
    createdOn: s.createdOn || '',
    createdBy: s.createdBy || '',
    lastUpdatedOn: s.lastUpdatedOn || '',
    lastUpdatedBy: s.lastUpdatedBy || '',
  };
}

function derivePublicFacingFromLegacy(tradeSubs, legacyPublicCategories = []) {
  if (!legacyPublicCategories.length) return tradeSubs.map(cloneSub);

  const bySource = new Map(
    legacyPublicCategories
      .filter((pc) => pc.sourceTradeSubcategoryId)
      .map((pc) => [pc.sourceTradeSubcategoryId, pc]),
  );

  return tradeSubs.map((sub) => {
    const next = cloneSub(sub);
    if (next.displayAsPublicFacing) return next;
    const linked = bySource.get(next.id);
    if (!linked) return next;
    return {
      ...next,
      displayAsPublicFacing: true,
      publicFacingName: next.publicFacingName || linked.name || next.name || '',
      publicFacingIconUrl: next.publicFacingIconUrl || linked.iconUrl || '',
      publicFacingIconFileName:
        next.publicFacingIconFileName ||
        linked.iconFileName ||
        `${trimName(next.name).toLowerCase().replace(/\s+/g, '-') || 'public'}-icon.svg`,
    };
  });
}

/** Public-Facing Name always equals Subcategory Name when public-facing. */
export function resolvePublicFacingName(sub) {
  return trimName(sub?.name);
}

function publicCategoryFromSubcategory(sub) {
  const sourceName = trimName(sub.name);
  const name = resolvePublicFacingName(sub);
  return {
    id: sub.id,
    name,
    iconUrl: sub.publicFacingIconUrl || '',
    iconFileName: sub.publicFacingIconFileName || '',
    sourceTradeSubcategoryId: sub.id,
    sourceTradeSubcategoryName: sourceName,
    subcategories: [],
    createdOn: sub.createdOn || '',
    createdBy: sub.createdBy || '',
    lastUpdatedOn: sub.lastUpdatedOn || '',
    lastUpdatedBy: sub.lastUpdatedBy || '',
  };
}

export function normalizeCategoryRecord(raw = {}) {
  const tradeSubcategories =
    raw.tradeSubcategories ||
    raw.directSubcategories ||
    (Array.isArray(raw.subcategories) ? raw.subcategories.filter((s) => !s.publicOnly) : []);

  let legacyPublicCategories = Array.isArray(raw.publicCategories)
    ? raw.publicCategories
    : [];

  if ((!legacyPublicCategories.length) && raw.publicCategory) {
    legacyPublicCategories = [raw.publicCategory];
  }

  const normalizedSubs = derivePublicFacingFromLegacy(
    tradeSubcategories,
    legacyPublicCategories,
  );

  const publicCategories = normalizedSubs
    .filter((s) => s.displayAsPublicFacing && trimName(s.name))
    .map(publicCategoryFromSubcategory);

  return {
    ...raw,
    tradeSubcategories: normalizedSubs,
    publicCategories,
    publicCategory: publicCategories[0] || null,
  };
}

export function getTradeSubcategories(category) {
  return category?.tradeSubcategories || category?.directSubcategories || [];
}

export function getCompletedTradeSubcategories(category) {
  return getTradeSubcategories(category).filter((s) => trimName(s.name));
}

/** Public-facing subcategories (source of truth). */
export function getPublicFacingSubcategories(category) {
  return getCompletedTradeSubcategories(category)
    .filter((s) => s.displayAsPublicFacing)
    .sort((a, b) => trimName(a.name).localeCompare(trimName(b.name)));
}

/**
 * Compatibility view of public-facing treatment as derived records.
 * Uses the original Subcategory ID (no separate Public-Facing ID).
 */
export function getPublicCategories(category) {
  if (Array.isArray(category?.tradeSubcategories) || Array.isArray(category?.directSubcategories)) {
    return getPublicFacingSubcategories(category).map(publicCategoryFromSubcategory);
  }
  if (Array.isArray(category?.publicCategories)) {
    return category.publicCategories.map((pc) => ({
      ...pc,
      subcategories: (pc.subcategories || []).map((s) => ({ ...s })),
    }));
  }
  if (category?.publicCategory) return [{ ...category.publicCategory }];
  return [];
}

export function getTradeSubcategoryNames(category) {
  return getCompletedTradeSubcategories(category)
    .map((s) => trimName(s.name))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

/** Subcategories column labels with IDs: "Name — ID" */
export function getTradeSubcategoryLabels(category) {
  return getCompletedTradeSubcategories(category)
    .slice()
    .sort((a, b) => trimName(a.name).localeCompare(trimName(b.name)))
    .map((s) => `${trimName(s.name)} — ${s.id || '—'}`);
}

/** Public-Facing Subcategories column: "Name — ID" */
export function getPublicFacingSubcategoryLabels(category) {
  return getPublicFacingSubcategories(category).map(
    (s) => `${resolvePublicFacingName(s)} — ${s.id || '—'}`,
  );
}

export function getPublicFacingNames(category) {
  return getPublicFacingSubcategories(category).map((s) => resolvePublicFacingName(s));
}

/** Public Subcategories column: underlying Subcategory names that are public-facing. */
export function getPublicSubcategorySourceNames(category) {
  return getPublicFacingSubcategories(category).map((s) => trimName(s.name)).filter(Boolean);
}

/** Public Icons column: icon filenames in the same order as Public-Facing. */
export function getPublicFacingIconFileNames(category) {
  return getPublicFacingSubcategories(category).map((s) => {
    return (
      s.publicFacingIconFileName ||
      `${resolvePublicFacingName(s).toLowerCase().replace(/\s+/g, '-') || 'icon'}.svg`
    );
  });
}

/** @deprecated Prefer getPublicFacingIconFileNames */
export function getPublicFacingIconLabels(category) {
  return getPublicFacingIconFileNames(category);
}

export function getPublicCategoryNames(category) {
  return getPublicFacingNames(category);
}

export function getPublicSubcategoryNames(category) {
  return getPublicCategories(category)
    .flatMap((pc) => pc.subcategories || [])
    .map((s) => trimName(s.name))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

export function countTradeSubcategories(category) {
  return getCompletedTradeSubcategories(category).length;
}

export function countPublicSubcategories(category) {
  return getPublicCategories(category).reduce(
    (sum, pc) => sum + (pc.subcategories || []).length,
    0,
  );
}

export function getLinkedPublicCategoryName(category, tradeSubcategoryId) {
  if (!tradeSubcategoryId) return '';
  const sub = getTradeSubcategories(category).find((s) => s.id === tradeSubcategoryId);
  if (sub?.displayAsPublicFacing) return resolvePublicFacingName(sub);
  return '';
}

export function getAvailableSourceTradeSubcategories(category, { excludePublicCategoryKey = null } = {}) {
  const tradeSubs = getCompletedTradeSubcategories(category).filter((s) => s.id);
  const linked = new Set(
    getPublicFacingSubcategories(category)
      .filter((s) => {
        const key = s.localKey || s.id;
        return key !== excludePublicCategoryKey;
      })
      .map((s) => s.id)
      .filter(Boolean),
  );
  return tradeSubs
    .filter((s) => !linked.has(s.id))
    .sort((a, b) => trimName(a.name).localeCompare(trimName(b.name)));
}

export function sortTradeCategoriesAlphabetically(categories) {
  return [...(categories || [])].sort((a, b) =>
    trimName(a.name).localeCompare(trimName(b.name)),
  );
}

/** Assign TC-0001, TC-0002… in alphabetical name order. */
export function assignAlphabeticalTradeCategoryIds(categories) {
  return sortTradeCategoriesAlphabetically(categories).map((category, index) => ({
    ...category,
    id: `TC-${String(index + 1).padStart(4, '0')}`,
  }));
}

export function formatTradeCategoryTableDate(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return formatTradeCategoryAuditDate(parsed);
  }
  return String(value);
}

export function formatTradeCategoryLastUpdated(category) {
  return formatTradeCategoryTableDate(category?.lastUpdatedOn);
}

export function matchesTradeCategorySearch(category, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  if (category.id?.toLowerCase().includes(q)) return true;
  if (trimName(category.name).toLowerCase().includes(q)) return true;
  for (const name of getPublicFacingNames(category)) {
    if (name.toLowerCase().includes(q)) return true;
  }
  for (const name of getTradeSubcategoryNames(category)) {
    if (name.toLowerCase().includes(q)) return true;
  }
  return false;
}

export function isIconFileSupported(file) {
  if (!file) return false;
  if (SUPPORTED_ICON_TYPES.includes(file.type)) return true;
  const lower = (file.name || '').toLowerCase();
  return SUPPORTED_ICON_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function collectAllSubcategoryNameEntries(allCategories = [], { excludeSubcategoryId = null } = {}) {
  const entries = [];
  (allCategories || []).forEach((c) => {
    getCompletedTradeSubcategories(c).forEach((s) => {
      if (excludeSubcategoryId && s.id === excludeSubcategoryId) return;
      entries.push({
        categoryId: c.id,
        subcategoryId: s.id,
        name: trimName(s.name),
        key: normName(s.name),
      });
    });
  });
  return entries;
}

/**
 * Save checks for Add / Edit.
 * Saves the category and its Subcategories together only when every check passes.
 */
export function collectSaveValidationErrors(
  formData,
  allCategories = [],
  { baseline = null, latestRecord = null } = {},
) {
  const errors = {};

  if (baseline?.id && latestRecord?.id) {
    const baselineStamp = String(baseline.lastUpdatedOn || '');
    const latestStamp = String(latestRecord.lastUpdatedOn || '');
    if (baselineStamp && latestStamp && baselineStamp !== latestStamp) {
      errors.concurrent = FIELD_ERRORS.concurrent;
    }
  }

  const name = trimName(formData.name);
  if (!name) {
    errors.name = FIELD_ERRORS.categoryName;
  } else {
    const dup = allCategories.some(
      (c) => c.id !== formData.id && normName(c.name) === normName(name),
    );
    if (dup) errors.name = FIELD_ERRORS.categoryNameExists;

    if (baseline?.id && trimName(baseline.name) && !retainsServiceMeaning(baseline.name, name)) {
      errors.name = FIELD_ERRORS.spellingOnly;
    }
  }

  if (!formData.iconUrl && !formData.iconFileName) {
    errors.icon = FIELD_ERRORS.categoryIcon;
  }

  const allBlocks = getTradeSubcategories(formData);
  const unfinished = allBlocks.filter((s) => !s.id && !trimName(s.name) && !s.displayAsPublicFacing);
  const retained = allBlocks.filter((s) => s.id || trimName(s.name) || s.displayAsPublicFacing);
  const completed = retained.filter((s) => trimName(s.name));

  if (unfinished.length > 0 && completed.length > 0) {
    unfinished.forEach((sub, index) => {
      const rowKey = sub.localKey || `unsaved-${index}`;
      errors[`tradeSub-${rowKey}`] = FIELD_ERRORS.unfinishedBlock;
    });
  }

  if (completed.length === 0) {
    errors.tradeSubcategories = FIELD_ERRORS.atLeastOneSubcategory;
  }

  const existingNames = new Set(
    collectAllSubcategoryNameEntries(allCategories, {}).map((e) => e.key),
  );
  if (baseline?.id) {
    getCompletedTradeSubcategories(baseline).forEach((s) => {
      if (s.id) existingNames.delete(normName(s.name));
    });
  }

  const baselineById = new Map(
    getCompletedTradeSubcategories(baseline || {})
      .filter((s) => s.id)
      .map((s) => [s.id, s]),
  );

  const tradeSubNames = new Set();
  retained.forEach((sub, index) => {
    const rowKey = sub.id || sub.localKey || `idx-${index}`;
    const errKey = `tradeSub-${rowKey}`;
    const subName = trimName(sub.name);

    if (!subName) {
      errors[errKey] = FIELD_ERRORS.subcategoryName;
      return;
    }

    const key = normName(subName);
    if (tradeSubNames.has(key)) {
      errors[errKey] = FIELD_ERRORS.subcategoryNameRepeated;
    } else if (existingNames.has(key)) {
      errors[errKey] = FIELD_ERRORS.subcategoryNameExists;
    }
    tradeSubNames.add(key);

    if (sub.id) {
      const previous = baselineById.get(sub.id);
      if (previous && !retainsServiceMeaning(previous.name, subName)) {
        errors[errKey] = FIELD_ERRORS.spellingOnly;
      }
    }

    if (sub.displayAsPublicFacing) {
      if (!sub.publicFacingIconUrl && !sub.publicFacingIconFileName) {
        errors[`tradeSub-${rowKey}-publicIcon`] = FIELD_ERRORS.publicIcon;
      }
    }
  });

  return { errors };
}

/** Mode B — Add Subcategory validation. */
export function collectAddSubcategoryValidationErrors(form, allCategories = []) {
  const errors = {};
  const subName = trimName(form?.name);
  if (!subName) {
    errors.name = 'Enter Subcategory Name.';
  } else {
    const exists = collectAllSubcategoryNameEntries(allCategories).some(
      (e) => e.key === normName(subName),
    );
    if (exists) errors.name = 'This Subcategory Name is already in use.';
  }

  if (form?.displayAsPublicFacing) {
    if (!resolvePublicFacingName(form)) {
      errors.publicFacingName = 'Enter Public-Facing Name.';
    }
    if (!form.publicFacingIconUrl && !form.publicFacingIconFileName) {
      errors.publicFacingIcon = 'Upload Public-Facing Icon.';
    }
  }

  return { errors };
}

/** Mode C — Select Public-Facing validation. */
export function collectSelectPublicFacingValidationErrors(form) {
  const errors = {};
  if (!form?.publicFacingIconUrl && !form?.publicFacingIconFileName) {
    errors.publicFacingIcon = 'Upload Public-Facing Icon.';
  }
  if (!trimName(form?.publicFacingName || form?.name)) {
    errors.publicFacingName = 'Enter Public-Facing Name.';
  }
  return { errors };
}

/** Mode D — Correct Spelling validation. */
export function collectSpellingCorrectionValidationErrors(
  form,
  allCategories = [],
  { recordType = 'Trade Category' } = {},
) {
  const errors = {};
  const current = trimName(form?.currentName);
  const corrected = trimName(form?.correctedSpelling);

  if (!corrected) {
    errors.correctedSpelling = 'Enter the corrected spelling.';
    return { errors };
  }

  if (normName(corrected) === normName(current)) {
    errors.correctedSpelling = 'Enter a corrected spelling that differs from the current name.';
    return { errors };
  }

  if (!retainsServiceMeaning(current, corrected)) {
    errors.correctedSpelling = MEANING_MESSAGES.spellingOnly;
    return { errors };
  }

  if (recordType === 'Trade Category') {
    const dup = allCategories.some(
      (c) => c.id !== form.permanentId && normName(c.name) === normName(corrected),
    );
    if (dup) errors.correctedSpelling = 'This Trade Category Name is already in use.';
  } else {
    const exists = collectAllSubcategoryNameEntries(allCategories, {
      excludeSubcategoryId: form.permanentId,
    }).some((e) => e.key === normName(corrected));
    if (exists) errors.correctedSpelling = 'This Subcategory Name is already in use.';
  }

  return { errors };
}

/** @deprecated Drafts are not part of the locked register model. */
export function collectDraftValidationErrors(formData, allCategories = []) {
  const errors = {};
  const name = trimName(formData.name);
  if (!name) {
    errors.name = 'Enter Trade Category Name.';
  } else {
    const dup = allCategories.some(
      (c) => c.id !== formData.id && normName(c.name) === normName(name),
    );
    if (dup) errors.name = 'This Trade Category Name is already in use.';
  }
  return { errors };
}

export function cloneTradeCategoryRecord(src) {
  const normalized = normalizeCategoryRecord(src || {});
  return {
    ...normalized,
    tradeSubcategories: normalized.tradeSubcategories.map(cloneSub),
    publicCategories: getPublicCategories(normalized),
    publicCategory: getPublicCategories(normalized)[0] || null,
    linkedRecords: { ...(normalized.linkedRecords || {}) },
  };
}

export function emptyTradeCategoryRecord() {
  return {
    id: '',
    name: '',
    iconUrl: '',
    iconFileName: '',
    status: '',
    tradeSubcategories: createInitialSubcategoryBlocks(),
    publicCategories: [],
    publicCategory: null,
    linkedRecords: {
      businesses: 0,
      livePosts: 0,
      draftPosts: 0,
    },
    createdOn: '',
    createdBy: '',
    lastUpdatedOn: '',
    lastUpdatedBy: '',
  };
}

/** @deprecated Public categories are derived from subcategories. */
export function emptyPublicCategoryDraft() {
  return {
    localKey: `pc-local-${Date.now()}`,
    id: '',
    name: '',
    iconUrl: '',
    iconFileName: '',
    sourceTradeSubcategoryId: '',
    subcategories: [],
    createdOn: '',
    createdBy: '',
    lastUpdatedOn: '',
    lastUpdatedBy: '',
  };
}

/** @deprecated Kept for import compatibility; public categories are never auto-created. */
export function tradeUsesPublicCategory() {
  return false;
}

/** @deprecated Kept for import compatibility. */
export function ensurePublicCategoryShell(formData) {
  return formData;
}

export function getPublicCategoryDisplayName(category) {
  return getPublicFacingNames(category).join(', ');
}

function readActivityLog() {
  try {
    const raw = sessionStorage.getItem(ACTIVITY_LOG_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeActivityLog(entries) {
  try {
    sessionStorage.setItem(ACTIVITY_LOG_KEY, JSON.stringify(entries.slice(0, 200)));
  } catch {
    // ignore storage failures in local/demo mode
  }
}

export function getTradeCategoryActivityEvents() {
  return readActivityLog();
}

export function recordTradeCategoryActivityEvent({
  event,
  tradeCategoryId = '',
  subcategoryId = '',
  previousSpelling = '',
  correctedSpelling = '',
  actor = DEFAULT_ACTOR,
  dateTime = formatTradeCategoryAuditDate(),
}) {
  const entry = {
    event,
    tradeCategoryId: tradeCategoryId || '—',
    subcategoryId: subcategoryId || '—',
    previousSpelling: previousSpelling || '—',
    correctedSpelling: correctedSpelling || '—',
    authorisedAdmin: actor,
    dateTime,
  };
  const next = [entry, ...readActivityLog()];
  writeActivityLog(next);
  return entry;
}

function subcategorySnapshot(sub) {
  return {
    id: sub.id || '',
    name: trimName(sub.name),
    displayAsPublicFacing: Boolean(sub.displayAsPublicFacing),
    publicFacingIconUrl: sub.publicFacingIconUrl || '',
    publicFacingIconFileName: sub.publicFacingIconFileName || '',
  };
}

export function hasTradeCategoryChanges(formData, baseline) {
  if (!baseline?.id) return true;
  if (trimName(formData.name) !== trimName(baseline.name)) return true;
  if ((formData.iconUrl || '') !== (baseline.iconUrl || '')) return true;
  if ((formData.iconFileName || '') !== (baseline.iconFileName || '')) return true;

  const nextSubs = getCompletedTradeSubcategories(formData).map(subcategorySnapshot);
  const prevSubs = getCompletedTradeSubcategories(baseline).map(subcategorySnapshot);
  if (nextSubs.length !== prevSubs.length) return true;
  return JSON.stringify(nextSubs) !== JSON.stringify(prevSubs);
}

/** Build a confirmed save payload (IDs, audit, completed subcategories only). */
export function buildConfirmedTradeCategorySave(
  formData,
  allCategories,
  { isAdd, actor = DEFAULT_ACTOR, baseline = null } = {},
) {
  const auditDateTime = formatTradeCategoryAuditDate();
  const payload = cloneTradeCategoryRecord(formData);
  const completed = getCompletedTradeSubcategories(payload);
  const changed = isAdd || hasTradeCategoryChanges(payload, baseline || formData);

  const existingTradeSubIds = allCategories
    .flatMap((c) => getTradeSubcategories(c).map((s) => s.id))
    .filter(Boolean);

  let tradeSubIds = [...existingTradeSubIds];
  const createdSubIds = [];

  const nextTradeSubs = completed.map((s) => {
    const base = {
      ...s,
      name: trimName(s.name),
      displayAsPublicFacing: Boolean(s.displayAsPublicFacing),
      publicFacingName: s.displayAsPublicFacing ? resolvePublicFacingName(s) : '',
      publicFacingIconUrl: s.displayAsPublicFacing ? s.publicFacingIconUrl || '' : '',
      publicFacingIconFileName: s.displayAsPublicFacing ? s.publicFacingIconFileName || '' : '',
      lastUpdatedOn: changed ? auditDateTime : s.lastUpdatedOn || '',
      lastUpdatedBy: changed ? actor : s.lastUpdatedBy || '',
      createdOn: s.createdOn || auditDateTime,
      createdBy: s.createdBy || actor,
    };

    if (s.id) return { ...base, localKey: '' };

    const id = nextTradeSubcategoryId(tradeSubIds);
    tradeSubIds.push(id);
    createdSubIds.push(id);
    return {
      ...base,
      id,
      localKey: '',
      createdOn: auditDateTime,
      createdBy: actor,
      lastUpdatedOn: auditDateTime,
      lastUpdatedBy: actor,
    };
  });

  const saved = cloneTradeCategoryRecord({
    ...payload,
    name: trimName(payload.name),
    tradeSubcategories: nextTradeSubs,
    status: CATEGORY_STATUS.ACTIVE,
    lastUpdatedOn: changed ? auditDateTime : payload.lastUpdatedOn || '',
    lastUpdatedBy: changed ? actor : payload.lastUpdatedBy || '',
  });

  if (isAdd || !saved.id) {
    saved.id = nextTradeCategoryId(allCategories);
    saved.createdOn = auditDateTime;
    saved.createdBy = actor;
    saved.lastUpdatedOn = auditDateTime;
    saved.lastUpdatedBy = actor;
  }

  if (isAdd) {
    recordTradeCategoryActivityEvent({
      event: ACTIVITY_EVENTS.CATEGORY_CREATED,
      tradeCategoryId: saved.id,
      actor,
      dateTime: auditDateTime,
    });
    createdSubIds.forEach((subId) => {
      recordTradeCategoryActivityEvent({
        event: ACTIVITY_EVENTS.SUBCATEGORY_CREATED_WITH_CATEGORY,
        tradeCategoryId: saved.id,
        subcategoryId: subId,
        actor,
        dateTime: auditDateTime,
      });
    });
    try {
      emitTradeCategoryHistory({
        isAdd: true,
        next: saved,
        actor,
        stamp: new Date().toISOString(),
        affectedSubcategoryIds: createdSubIds,
        activityDetails: createdSubIds.length
          ? `Trade Category created with ${createdSubIds.length} Subcategory(ies)`
          : 'Trade Category created',
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
  } else if (changed) {
    recordTradeCategoryActivityEvent({
      event: ACTIVITY_EVENTS.CATEGORY_UPDATED,
      tradeCategoryId: saved.id,
      actor,
      dateTime: auditDateTime,
    });
    createdSubIds.forEach((subId) => {
      recordTradeCategoryActivityEvent({
        event: ACTIVITY_EVENTS.SUBCATEGORY_ADDED,
        tradeCategoryId: saved.id,
        subcategoryId: subId,
        actor,
        dateTime: auditDateTime,
      });
    });
    try {
      emitTradeCategoryHistory({
        isAdd: false,
        previous: baseline || null,
        next: saved,
        actor,
        stamp: new Date().toISOString(),
        affectedSubcategoryIds: createdSubIds,
        activityDetails: createdSubIds.length
          ? `Category updated; Subcategory IDs added: ${createdSubIds.join(', ')}`
          : 'Trade Category updated',
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
  }

  return saved;
}

/** Mode B — add one subcategory under an existing parent. */
export function buildAddSubcategorySave(category, subcategoryForm, allCategories, { actor = DEFAULT_ACTOR } = {}) {
  const auditDate = formatTradeCategoryListDate();
  const auditDateTime = formatTradeCategoryAuditDate();
  const existing = cloneTradeCategoryRecord(category);
  const existingTradeSubIds = allCategories
    .flatMap((c) => getTradeSubcategories(c).map((s) => s.id))
    .filter(Boolean);

  const id = nextTradeSubcategoryId(existingTradeSubIds);
  const nextSub = {
    ...emptySubcategoryBlock(),
    localKey: '',
    id,
    name: trimName(subcategoryForm.name),
    displayAsPublicFacing: Boolean(subcategoryForm.displayAsPublicFacing),
    publicFacingName: subcategoryForm.displayAsPublicFacing
      ? resolvePublicFacingName(subcategoryForm)
      : '',
    publicFacingIconUrl: subcategoryForm.publicFacingIconUrl || '',
    publicFacingIconFileName: subcategoryForm.publicFacingIconFileName || '',
    createdOn: auditDate,
    createdBy: actor,
    lastUpdatedOn: auditDate,
    lastUpdatedBy: actor,
  };

  const saved = cloneTradeCategoryRecord({
    ...existing,
    tradeSubcategories: [...getTradeSubcategories(existing), nextSub],
    lastUpdatedOn: auditDate,
    lastUpdatedBy: actor,
  });

  recordTradeCategoryActivityEvent({
    event: ACTIVITY_EVENTS.SUBCATEGORY_ADDED,
    tradeCategoryId: saved.id,
    subcategoryId: id,
    actor,
    dateTime: auditDateTime,
  });
  try {
    emitTradeCategoryHistory({
      isAdd: false,
      previous: existing,
      next: saved,
      actor,
      stamp: new Date().toISOString(),
      affectedSubcategoryIds: [id],
      activityDetails: `Subcategory added: ${nextSub.name} (${id})`,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }

  return saved;
}

/** Mode C — select existing subcategory as public-facing. */
export function buildSelectPublicFacingSave(category, subcategoryId, iconForm, { actor = DEFAULT_ACTOR } = {}) {
  const auditDate = formatTradeCategoryListDate();
  const auditDateTime = formatTradeCategoryAuditDate();
  const existing = cloneTradeCategoryRecord(category);

  const nextSubs = getTradeSubcategories(existing).map((s) => {
    if (s.id !== subcategoryId) return s;
    return {
      ...s,
      displayAsPublicFacing: true,
      publicFacingName: resolvePublicFacingName({
        ...s,
        publicFacingName: iconForm.publicFacingName,
      }),
      publicFacingIconUrl: iconForm.publicFacingIconUrl || '',
      publicFacingIconFileName: iconForm.publicFacingIconFileName || '',
      lastUpdatedOn: auditDate,
      lastUpdatedBy: actor,
    };
  });

  const saved = cloneTradeCategoryRecord({
    ...existing,
    tradeSubcategories: nextSubs,
    lastUpdatedOn: auditDate,
    lastUpdatedBy: actor,
  });

  recordTradeCategoryActivityEvent({
    event: ACTIVITY_EVENTS.CATEGORY_UPDATED,
    tradeCategoryId: saved.id,
    subcategoryId,
    actor,
    dateTime: auditDateTime,
  });
  try {
    emitTradeCategoryHistory({
      isAdd: false,
      previous: existing,
      next: saved,
      actor,
      stamp: new Date().toISOString(),
      affectedSubcategoryIds: [subcategoryId],
      activityDetails: `Public-facing selection updated for Subcategory ${subcategoryId}`,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }

  return saved;
}

/** Mode D — spelling correction for category or subcategory. */
export function buildSpellingCorrectionSave(
  category,
  { recordType, permanentId, correctedSpelling },
  { actor = DEFAULT_ACTOR } = {},
) {
  const auditDate = formatTradeCategoryListDate();
  const auditDateTime = formatTradeCategoryAuditDate();
  const existing = cloneTradeCategoryRecord(category);
  const corrected = trimName(correctedSpelling);

  if (recordType === 'Trade Category') {
    const previous = trimName(existing.name);
    const saved = cloneTradeCategoryRecord({
      ...existing,
      name: corrected,
      lastUpdatedOn: auditDate,
      lastUpdatedBy: actor,
    });
    recordTradeCategoryActivityEvent({
      event: ACTIVITY_EVENTS.CATEGORY_UPDATED,
      tradeCategoryId: saved.id,
      previousSpelling: previous,
      correctedSpelling: corrected,
      actor,
      dateTime: auditDateTime,
    });
    try {
      emitTradeCategoryHistory({
        isAdd: false,
        previous: existing,
        next: saved,
        actor,
        stamp: new Date().toISOString(),
        activityDetails: `Spelling corrected: ${previous} → ${corrected}`,
      });
    } catch {
      /* history recording failure must not reverse the source save */
    }
    return saved;
  }

  let previous = '';
  const nextSubs = getTradeSubcategories(existing).map((s) => {
    if (s.id !== permanentId) return s;
    previous = trimName(s.name);
    return {
      ...s,
      name: corrected,
      lastUpdatedOn: auditDate,
      lastUpdatedBy: actor,
    };
  });

  const saved = cloneTradeCategoryRecord({
    ...existing,
    tradeSubcategories: nextSubs,
    lastUpdatedOn: auditDate,
    lastUpdatedBy: actor,
  });

  recordTradeCategoryActivityEvent({
    event: ACTIVITY_EVENTS.CATEGORY_UPDATED,
    tradeCategoryId: saved.id,
    subcategoryId: permanentId,
    previousSpelling: previous,
    correctedSpelling: corrected,
    actor,
    dateTime: auditDateTime,
  });
  try {
    emitTradeCategoryHistory({
      isAdd: false,
      previous: existing,
      next: saved,
      actor,
      stamp: new Date().toISOString(),
      affectedSubcategoryIds: [permanentId],
      activityDetails: `Subcategory spelling corrected: ${previous} → ${corrected}`,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }

  return saved;
}

/** @deprecated Drafts are not part of the locked register model. */
export function buildConfirmedTradeCategoryDraft(formData, allCategories, { isAdd, actor = DEFAULT_ACTOR } = {}) {
  const auditDate = formatTradeCategoryListDate();
  const payload = cloneTradeCategoryRecord(formData);
  const namedSubs = getTradeSubcategories(payload).filter((s) => trimName(s.name));

  const existingTradeSubIds = allCategories
    .flatMap((c) => getTradeSubcategories(c).map((s) => s.id))
    .filter(Boolean);

  let tradeSubIds = [...existingTradeSubIds];

  const nextTradeSubs = namedSubs.map((s) => {
    const base = {
      ...s,
      name: trimName(s.name),
      displayAsPublicFacing: Boolean(s.displayAsPublicFacing),
      publicFacingName: s.displayAsPublicFacing ? resolvePublicFacingName(s) : '',
      publicFacingIconUrl: s.publicFacingIconUrl || '',
      publicFacingIconFileName: s.publicFacingIconFileName || '',
      lastUpdatedOn: auditDate,
      lastUpdatedBy: actor,
      createdOn: s.createdOn || auditDate,
      createdBy: s.createdBy || actor,
    };

    if (s.id) return { ...base, localKey: '' };

    const id = nextTradeSubcategoryId(tradeSubIds);
    tradeSubIds.push(id);
    return {
      ...base,
      id,
      localKey: '',
      createdOn: auditDate,
      createdBy: actor,
    };
  });

  const saved = cloneTradeCategoryRecord({
    ...payload,
    name: trimName(payload.name),
    tradeSubcategories: nextTradeSubs,
    status: CATEGORY_STATUS.DRAFT,
    lastUpdatedOn: auditDate,
    lastUpdatedBy: actor,
  });

  if (isAdd || !saved.id) {
    saved.id = nextTradeCategoryId(allCategories);
    saved.createdOn = auditDate;
    saved.createdBy = actor;
  }

  return saved;
}
